import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FieldMetaType } from '@/types/myVaultTypes';
import { ResponseErrorType } from '@/types/sharedTypes';
import { useQueryClient } from '@tanstack/react-query';
import { useField } from 'formik';
import { useEffect, useState } from 'react';
import { loadBankName } from '../../../DepositAccountsPage/_CreateDepositAccountPage/api/api';
import VerifiedIcon from '@/images/VerifiedIcon.svg?react';

type BankNameFieldType = {
  wide?: boolean;
  field: Omit<FieldMetaType, 'rails'>;
  placeholder: string;
  apiError?: ResponseErrorType;
  label: string;
};

const INITIAL_BANK_VERIFICATION_STATE = {
  isVerifiedBank: false,
  isDisabledBankName: false,
};

type BankVerificationType = typeof INITIAL_BANK_VERIFICATION_STATE;

const ROUTING_NUMBER_LENGTH = 9;

export const BankNameField = ({ field, wide, placeholder, apiError, label }: BankNameFieldType) => {
  const queryClient = useQueryClient();
  const [fieldProps] = useField(`fields.wire_routing`);
  const [_, __, { setValue }] = useField(`fields.bank_name`);
  const [bankVerificationState, setBankVerificationState] = useState(INITIAL_BANK_VERIFICATION_STATE);

  const updateBankVerificationState = (newState: Partial<BankVerificationType>) => {
    setBankVerificationState((state) => ({ ...state, ...newState }));
  };

  const loadBankNameByRoutingNumber = async (routingNumber: string) => {
    try {
      return await queryClient.fetchQuery({
        queryKey: ['load_bank_name', routingNumber],
        queryFn: () => loadBankName(routingNumber),
      });
    } catch (error) {
      return null;
    }
  };

  const setVerifiedBankName = (routingNumber: string) => {
    loadBankNameByRoutingNumber(routingNumber)
      .then((response) => {
        if (!response || response.error) {
          return;
        }

        const bankName = response.body?.[0]?.bank_name;

        if (bankName) {
          updateBankVerificationState({ isVerifiedBank: true, isDisabledBankName: true });
          setValue(bankName);
        } else {
          updateBankVerificationState({ isVerifiedBank: false, isDisabledBankName: false });
        }
      })
      .catch(() => {
        updateBankVerificationState({ isVerifiedBank: false, isDisabledBankName: false });
      });
  };

  useEffect(() => {
    const routingNumber = fieldProps.value?.trim() || '';

    if (!routingNumber || routingNumber.length !== ROUTING_NUMBER_LENGTH) {
      queryClient.cancelQueries({ queryKey: ['load_bank_name'] });
      updateBankVerificationState({ isVerifiedBank: false, isDisabledBankName: false });

      return;
    }

    setVerifiedBankName(routingNumber);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldProps.value]);

  return (
    <div key={field.key} style={wide ? { gridColumn: 'span 2' } : undefined}>
      <FormikInput
        name={`fields.${field.key}`}
        label={label}
        placeholder={placeholder}
        autoComplete='new-field'
        apiError={apiError}
        disabled={bankVerificationState.isDisabledBankName}
        after={bankVerificationState.isVerifiedBank && <VerifiedIcon />}
      />
    </div>
  );
};
